import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { personalizationApi } from "@/api/personalizationClient";
import { usePersonalization } from "@/components/personalization/PersonalizationContext";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import PersonalizedMarketTrendCard from "@/components/personalization/PersonalizedMarketTrendCard";
import ComplianceLock from "@/components/compliance/ComplianceLock";
import { PALETTE } from "@/components/animation/MotionShared";

/**
 * Logix2PersonalRelationshipPanel — the user-facing personalized briefing
 * panel. Pulls recommendations from /api/oracle/personalization/recommendations
 * and renders one PersonalizedMarketTrendCard per item. Greets the user by
 * relationship.greeting and shows the relationship tone + archetype.
 *
 * Strictly additive; works alongside Logix2ReasoningPanel + IntelligenceConsole.
 */
export default function Logix2PersonalRelationshipPanel({
  testid = "logix2-personal-relationship-panel",
  limit = 6,
}) {
  const reduce = useReducedMotionSafe();
  const { relationship, profile, isAuthed, postEvent, patch } = usePersonalization();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!isAuthed) { setData(null); return; }
    setBusy(true); setErr("");
    try {
      const { data: payload } = await personalizationApi.recommendations(limit);
      setData(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "recommendations_failed");
    } finally { setBusy(false); }
  }, [isAuthed, limit]);

  useEffect(() => { load(); }, [load]);

  // Refresh whenever the user's profile changes (e.g. after preference save)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (profile) load(); }, [profile?.last_updated, load]);

  const addToWatchlist = useCallback(async (rec) => {
    if (!profile || !patch) return;
    const sym = (rec?.symbol || "").toString().toUpperCase();
    if (!sym) return;
    const next = Array.from(new Set([...(profile.watchlist || []).map((s) => s.toUpperCase()), sym])).slice(0, 60);
    await patch({ watchlist: next });
    postEvent("watchlist_add", { symbol: sym, source: "personal_relationship_panel" });
    setTimeout(() => { load(); }, 400);
  }, [profile, patch, postEvent, load]);

  if (!isAuthed) {
    return (
      <section style={S.shell} data-testid={testid}>
        <header style={S.head}>
          <div>
            <span style={S.tagline}>LOGIX-2™ Personal Relationship Engine™</span>
            <h3 style={S.title}>Sign in to unlock personalized briefings</h3>
          </div>
        </header>
        <p style={S.lede}>
          When you sign in, LOGIX-2 learns your preferred markets, regions, risk style, and watchlist —
          then explains why each opportunity fits you.
        </p>
        <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
      </section>
    );
  }

  const greeting = relationship?.greeting || "Welcome to CareLogix ORACLE.";
  const tone     = relationship?.tone || "concise educational";
  const archetype= data?.archetype || relationship?.archetype || "Member";

  return (
    <section style={S.shell} data-testid={testid}>
      <ComplianceLock variant="banner" policy="v27" testid={`${testid}-compliance-top`} />
      <header style={S.head}>
        <div>
          <span style={S.tagline}>LOGIX-2™ Personal Relationship Engine™</span>
          <motion.h3
            style={S.title}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.45 }}
            data-testid={`${testid}-greeting`}
          >
            {greeting}
          </motion.h3>
        </div>
        <div style={S.headerRight}>
          <span style={S.chip} data-testid={`${testid}-archetype`}>{archetype.toUpperCase()}</span>
          <span style={S.chip} data-testid={`${testid}-tone`}>{tone.toUpperCase()}</span>
          <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>{busy ? "…" : "↻"}</button>
        </div>
      </header>

      <p style={S.context} data-testid={`${testid}-context`}>
        <strong style={S.contextLbl}>Personal context:</strong>{" "}
        {data?.profile_summary || "Building your profile from your watchlist, recent searches, preferred regions, and risk profile."}
      </p>

      {err && <div style={S.err}>⚠ {err}</div>}

      {data?.items?.length === 0 && (
        <div style={S.empty} data-testid={`${testid}-empty`}>
          No personalized signals yet. Open the preferences modal to tune your interests.
        </div>
      )}

      <div style={S.grid}>
        {(data?.items || []).map((rec, i) => (
          <PersonalizedMarketTrendCard
            key={`${rec.symbol}-${i}`}
            rec={rec}
            onWatchlistAdd={addToWatchlist}
            testid={`${testid}-card-${i}`}
          />
        ))}
      </div>

      <span style={S.compliance}>
        {data?.disclaimer || "Personalization is decision-support only — not financial advice. No guarantees. Human decision required."}
      </span>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance-footer`} />
    </section>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.93))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 14, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', system-ui, sans-serif", maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: PALETTE.goldBright, margin: 0, fontSize: 17, lineHeight: 1.15 },
  headerRight: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  chip: { color: PALETTE.gold, border: `1px solid ${PALETTE.goldGlow}`, padding: "3px 10px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", fontWeight: 800, background: "rgba(197,160,89,0.08)" },
  refresh: { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "4px 10px", borderRadius: 8, fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, cursor: "pointer" },
  lede: { color: PALETTE.slate3, fontSize: 13, lineHeight: 1.5, margin: 0 },
  context: { color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.55, margin: 0, padding: "8px 10px", background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10 },
  contextLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em", marginRight: 6 },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  empty: { padding: "8px 12px", background: "rgba(252,211,77,0.10)", border: "1px solid rgba(252,211,77,0.30)", color: "#fde68a", borderRadius: 10, fontSize: 12 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.06em" },
};
